import React, { useState, useMemo } from 'react';
import { Table, Form, InputGroup, Button, Row, Col, Pagination } from 'react-bootstrap';
import * as XLSX from 'xlsx';

const DeviceOrderTable = ({ data, onDataChange }) => {
  // State management
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Column definitions
  const columns = [
    { key: 'id', label: 'ID' },
    { key: 'status', label: 'Status' },
    { key: 'deviceModel', label: 'Device Model' },
    { key: 'quantity', label: 'Quantity' },
    { key: 'price', label: 'Price (₹)' },
    { key: 'total', label: 'Total (₹)' },
    { key: 'address', label: 'Address' },
    { key: 'tid', label: 'TID' },
    { key: 'shopName', label: 'Shop Name' },
    { key: 'mobile', label: 'Mobile' },
    { key: 'panNumber', label: 'Pan Number' },
    { key: 'type', label: 'Type' },
    { key: 'uniqueId', label: 'Unique ID' }
  ];

  // Filter data based on search
  const filteredData = useMemo(() => {
    let filtered = [...data];

    // Global search
    if (searchQuery) {
      filtered = filtered.filter(row =>
        Object.values(row).some(value =>
          String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
      );
    }

    return filtered;
  }, [data, searchQuery]);

  // Pagination logic
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentData = filteredData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page
  };

  // Export to Excel
  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'DeviceOrders');
    XLSX.writeFile(workbook, `DeviceOrders_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  return (
    <div className="user-table-container">
      {/* Header with Title, Search and Export */}
      <Row className="mb-3 g-2 align-items-center">
        <Col lg={4} md={6} sm={12}>
          <h4 className="mb-0" style={{ color: '#1e293b', fontWeight: '700', fontSize: '1.5rem' }}>
            Device Orders
          </h4>
        </Col>
        <Col lg={8} md={6} sm={12} className="d-flex justify-content-end gap-2">
          <InputGroup style={{ maxWidth: '300px' }}>
            <InputGroup.Text>
              <i className="bi bi-search"></i>
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </InputGroup>
          <Button 
            size="sm"
            variant="success" 
            onClick={exportToExcel}
            style={{
              padding: '0.4rem 1rem 0.5rem 1rem',
              fontSize: '0.875rem',
              height: 'auto'
            }}
          >
            <i className="bi bi-file-earmark-excel me-1"></i> Excel
          </Button>
        </Col>
      </Row>

      {/* Table */}
      <div className="table-responsive" style={{ background: '#f0f2f5', borderRadius: '12px', padding: '0' }}>
        <Table bordered hover className="modern-table" style={{ background: '#f0f2f5' }}>
          <thead style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <tr>
              {columns.map((column) => (
                <th key={column.key} style={{ padding: '12px 16px' }}>{column.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {currentData.length > 0 ? (
              currentData.map((row, index) => (
                <tr key={index} style={{ background: '#f0f2f5' }}>
                  <td style={{ padding: '12px 16px' }}>{row.id}</td>
                  <td style={{ padding: '12px 16px' }}>{row.status}</td>
                  <td style={{ padding: '12px 16px' }}>{row.deviceModel}</td>
                  <td className="text-center fw-bold" style={{ padding: '12px 16px' }}>{row.quantity}</td>
                  <td className="fw-bold text-primary" style={{ padding: '12px 16px' }}>₹{row.price.toLocaleString()}</td>
                  <td className="fw-bold text-success" style={{ padding: '12px 16px' }}>₹{row.total.toLocaleString()}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <details>
                      <summary style={{ cursor: 'pointer', color: '#3b82f6' }}>
                        View Address
                      </summary>
                      <div style={{ marginTop: '8px', fontSize: '0.9rem', color: '#475569' }}>
                        {row.address}
                      </div>
                    </details>
                  </td>
                  <td style={{ padding: '12px 16px' }}>{row.tid}</td>
                  <td style={{ padding: '12px 16px' }}>{row.shopName}</td>
                  <td style={{ padding: '12px 16px' }}>{row.mobile}</td>
                  <td style={{ padding: '12px 16px' }}>{row.panNumber}</td>
                  <td style={{ padding: '12px 16px' }}>{row.type}</td>
                  <td style={{ padding: '12px 16px' }}>{row.uniqueId}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} className="text-center py-4">
                  <i className="bi bi-inbox fs-1 text-muted d-block mb-2"></i>
                  <span className="text-muted">No device orders found</span>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Pagination Controls */}
      <Row className="mt-3 align-items-center">
        <Col md={6}>
          <span className="text-muted">
            Showing {indexOfFirstRow + 1} to {Math.min(indexOfLastRow, filteredData.length)} of {filteredData.length} entries
          </span>
        </Col>
        <Col md={6}>
          <Pagination className="justify-content-end mb-0">
            <Pagination.First 
              onClick={() => handlePageChange(1)} 
              disabled={currentPage === 1}
            />
            <Pagination.Prev 
              onClick={() => handlePageChange(currentPage - 1)} 
              disabled={currentPage === 1}
            />
            
            {[...Array(totalPages)].map((_, index) => {
              const pageNumber = index + 1;
              // Show first page, last page, current page, and pages around current page
              if (
                pageNumber === 1 ||
                pageNumber === totalPages ||
                (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
              ) {
                return (
                  <Pagination.Item
                    key={pageNumber}
                    active={pageNumber === currentPage}
                    onClick={() => handlePageChange(pageNumber)}
                  >
                    {pageNumber}
                  </Pagination.Item>
                );
              } else if (
                pageNumber === currentPage - 2 ||
                pageNumber === currentPage + 2
              ) {
                return <Pagination.Ellipsis key={pageNumber} disabled />;
              }
              return null;
            })}
            
            <Pagination.Next 
              onClick={() => handlePageChange(currentPage + 1)} 
              disabled={currentPage === totalPages}
            />
            <Pagination.Last 
              onClick={() => handlePageChange(totalPages)} 
              disabled={currentPage === totalPages}
            />
          </Pagination>
        </Col>
      </Row>
    </div>
  );
};

export default DeviceOrderTable;

